import { useEffect, useMemo } from 'react';
import { useGlobalLoading } from '@/stores/loadingStore';
import type { Application, JobPosition, JobPosting } from '@/types';
import { mergeApplicationsForAnalytics } from '@/lib/applicationArchive';
import {
  COMPANY_APPLICATIONS_INITIAL_PREFETCH_PAGES,
  COMPANY_APPLICATIONS_WARM_LIST_SCOPE,
  flattenApplicationPages,
} from '@/lib/companyApplicationsList';
import {
  useCompanyApplicationsInfiniteQuery,
  usePositionsQuery,
  usePostingsAllQuery,
} from '@/hooks/useRecruiterListQueries';

export interface UseDashboardDataReturn {
  positions: JobPosition[];
  postings: JobPosting[];
  applications: Application[];
  /** True until the first `applications-list` pages are available (no legacy hydrate). */
  applicationsHydrating: boolean;
  loading: boolean;
  error: string | null;
}

/**
 * Dashboard data without `useApplicationsQuery` / progressive hydrate.
 * Active applications come from the same `applications-list` cache as Applications (warm scope).
 * Archived outcomes in analytics stay empty until `pipeline-counts` (Fase 2a).
 */
export function useDashboardData(companyId: string | undefined): UseDashboardDataReturn {
  const positionsQuery = usePositionsQuery(companyId);
  const postingsQuery = usePostingsAllQuery(companyId);
  const positions = positionsQuery.data ?? [];

  const listQuery = useCompanyApplicationsInfiniteQuery(
    companyId,
    positions,
    COMPANY_APPLICATIONS_WARM_LIST_SCOPE,
    !!companyId && positionsQuery.isSuccess,
  );

  const activeApplications = useMemo(
    () => flattenApplicationPages(listQuery.data?.pages),
    [listQuery.data?.pages],
  );

  const applicationsHydrating =
    !!companyId
    && listQuery.isPending
    && activeApplications.length === 0;

  useEffect(() => {
    if (!companyId || !listQuery.hasNextPage || listQuery.isFetchingNextPage) return;
    const pagesLoaded = listQuery.data?.pages.length ?? 0;
    if (pagesLoaded < COMPANY_APPLICATIONS_INITIAL_PREFETCH_PAGES) {
      void listQuery.fetchNextPage();
    }
  }, [
    companyId,
    listQuery.hasNextPage,
    listQuery.isFetchingNextPage,
    listQuery.data?.pages.length,
    listQuery,
  ]);

  const applications = useMemo(
    () => mergeApplicationsForAnalytics(activeApplications, []),
    [activeApplications],
  );

  const loading = !!companyId && (
    positionsQuery.isLoading
    || postingsQuery.isLoading
    || applicationsHydrating
  );

  const error = useMemo(() => {
    const err = positionsQuery.error
      ?? postingsQuery.error
      ?? listQuery.error;
    if (!err) return null;
    return err instanceof Error ? err.message : 'Failed to load dashboard data';
  }, [
    positionsQuery.error,
    postingsQuery.error,
    listQuery.error,
  ]);

  useGlobalLoading(loading, 'dashboard');

  return {
    positions,
    postings: postingsQuery.data ?? [],
    applications,
    applicationsHydrating,
    loading,
    error,
  };
}
